import { Component, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { AuthService, UserData } from '../services/auth.service';
import { ThemeService } from '../services/theme.service';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    RouterLink
  ],
  template: `
    <mat-toolbar color="primary" class="custom-toolbar mat-elevation-z2">
      @if (isMobile()) {
        <button
          mat-icon-button
          (click)="toggleSidenav.emit()"
          aria-label="Toggle Sidenav"
          class="sidenav-toggle"
        >
          <mat-icon>menu</mat-icon>
        </button>
      }

      <div class="logo-container" routerLink="/">
        <svg viewBox="0 0 115 50" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="m0 0v15h10v35h15v-35h10v-15z"/>
            <path d="m40 0v50h35v-20h-15v5h-5v-20h5v5l15-5v-15z"/>
            <path d="m80 0v50h15v-20h5v20h15v-50h-15v20h-5v-20z"/>
          </g>
        </svg>
        <span class="title">Mileage Tracker</span>
      </div>

      @if (user()) {
        <button
          mat-button
          [matMenuTriggerFor]="userMenu"
          class="profile-button"
        >
          <mat-icon>person</mat-icon>
          <span>Profile</span>
        </button>

        <mat-menu #userMenu="matMenu" xPosition="before" class="user-menu">
          <div class="menu-header">
            <div class="menu-user-details">
              <span class="menu-name">{{ user()?.displayName }}</span>
              <span class="menu-email">{{ user()?.email }}</span>
              <span class="menu-role">{{ user()?.role | titlecase }}</span>
            </div>
          </div>
          <mat-divider></mat-divider>
          
          <button mat-menu-item (click)="toggleTheme()">
            <mat-icon>{{ themeService.isDarkMode() ? 'light_mode' : 'dark_mode' }}</mat-icon>
            <span>{{ themeService.isDarkMode() ? 'Light Mode' : 'Dark Mode' }}</span>
          </button>

          <mat-divider></mat-divider>
          
          <button mat-menu-item (click)="logout()">
            <mat-icon>logout</mat-icon>
            <span>Sign Out</span>
          </button>
        </mat-menu>
      }
    </mat-toolbar>
  `,
  styles: [`
    .custom-toolbar {
      display: flex;
      justify-content: space-between;
      height: 64px;
      position: sticky;
      top: 0;
      z-index: 1000;
      background-color: #FFF100; /* Yellow Primary 90 */
      color: #000000; /* Black Primary 0 */
    }

    .logo-container {
      display: flex;
      align-items: center;
      flex-grow: 1;
      justify-content: center;
      cursor: pointer;
    }

    svg {
      height: 40px;
      width: auto;
      fill: #000000;
    }

    .title {
      font-size: 32px;
      font-family: 'Anton', sans-serif;
      font-weight: bold;
      margin-left: 16px;
      letter-spacing: .15rem;
      text-transform: uppercase;
    }

    .profile-button {
      display: flex;
      align-items: center;
      color: #000000 !important;
      margin-right: 16px;
    }

    .sidenav-toggle {
      color: #000000 !important;
    }

    .profile-button mat-icon {
      margin-right: 8px;
    }

    @media (max-width: 600px) {
      .title {
        font-size: 20px;
        margin-left: 8px;
      }
      svg {
        height: 30px;
      }
    }
  `]
})
export class HeaderComponent {
  user = input<UserData | null>(null);
  isMobile = input<boolean>(false);
  toggleSidenav = output<void>();

  protected readonly themeService = inject(ThemeService);
  private readonly authService = inject(AuthService);

  async toggleTheme() {
    const isDark = !this.themeService.isDarkMode();
    this.themeService.setDarkMode(isDark);
    await this.authService.updateThemePreference(isDark ? 'dark' : 'light');
  }

  logout() {
    this.authService.logout();
  }
}
